import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode } from "react";
import { Box, Breadcrumb, Flex, Heading, HStack, Skeleton, SkeletonText, Stack, Text } from "@chakra-ui/react";

type BreadcrumbItem = {
  /** Empty while it is a name still on its way: drawn as a skeleton. */
  label: string;
  to?: string;
  search?: Record<string, string>;
};

type PageHeaderProps = {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
  /** Rendered to the left of the title -- an avatar, typically. */
  leading?: ReactNode;
  /**
   * Facts about the page's subject, under its description: a record's
   * status, dates and tags belong with its name rather than in a card of
   * their own below the header.
   */
  meta?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /**
   * The page's subject is still on its way. The title and the current
   * breadcrumb are drawn as skeletons rather than as a placeholder -- a slug
   * or a "Loading…" that is then replaced by the real name a moment later
   * reads as a flicker. A description passed while loading stays visible;
   * an empty one is drawn as a skeleton too.
   */
  loading?: boolean;
};

export function PageHeader({ eyebrow, title, description, actions, leading, meta, breadcrumbs, loading = false }: PageHeaderProps) {
  const resolvedBreadcrumbs = breadcrumbs ?? (eyebrow ? [{ label: "CeMMbase", to: "/" }, { label: eyebrow }] : [{ label: "CeMMbase" }]);

  return (
    <Box
      borderBottomWidth="1px"
      borderColor="border"
      bg="bg"
      px={{ base: 6, sm: 8, lg: 10 }}
      py={6}
    >
      <Stack gap={6}>
        <Breadcrumb.Root
          size="sm"
          maxW="full"
          overflowX="auto"
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.18em"
          color="fg.muted"
        >
          <Breadcrumb.List>
            {resolvedBreadcrumbs.map((item, index) => (
              <Fragment key={`${item.label}-${index}`}>
                <Breadcrumb.Item>
                  {loading && index > 0 && (!item.to || item.label === "") ? (
                    <Skeleton height="3" width="40" />
                  ) : item.to ? (
                    <Breadcrumb.Link asChild>
                      <Link to={item.to} search={item.search}>{item.label}</Link>
                    </Breadcrumb.Link>
                  ) : (
                    <Breadcrumb.CurrentLink>{item.label}</Breadcrumb.CurrentLink>
                  )}
                </Breadcrumb.Item>
                {index < resolvedBreadcrumbs.length - 1 ? <Breadcrumb.Separator /> : null}
              </Fragment>
            ))}
          </Breadcrumb.List>
        </Breadcrumb.Root>

        <Flex
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "flex-end" }}
          justify="space-between"
          gap={4}
        >
          <HStack gap={5} align="center">
            {leading}
            <Stack gap={2}>
              {loading ? (
                // Sized to the heading's line, so the page does not move when
                // the name arrives.
                <Skeleton height={{ base: "6", sm: "7" }} width={{ base: "64", sm: "96" }} />
              ) : (
                <Heading size={{ base: "2xl", sm: "3xl" }} lineHeight="1">{title}</Heading>
              )}
              {loading && !description ? (
                <SkeletonText noOfLines={1} width={{ base: "72", sm: "md" }} />
              ) : (
                <Text maxW="3xl" fontSize={{ base: "sm", sm: "md" }} color="fg.muted">{description}</Text>
              )}
              {meta === undefined ? null : <Box pt={1}>{meta}</Box>}
            </Stack>
          </HStack>
          {actions ? <HStack flexWrap="wrap" gap={3}>{actions}</HStack> : null}
        </Flex>
      </Stack>
    </Box>
  );
}
